<script lang="ts">
  import Screen from '../lib/design/Screen.svelte';
  import { link, href } from '../lib/router.svelte';
  import { session } from '../lib/session.svelte';
  import { capture } from '../lib/capture.svelte';
  import { humanize } from '../shared/slug.js';

  let text = $state('');
  let justSaved = $state(false);

  async function save() {
    const t = text.trim();
    if (!t) return;
    text = '';
    justSaved = true;
    setTimeout(() => (justSaved = false), 2200);
    await capture.save(t);
  }
</script>

<Screen title="Capture" sync={capture.pending ? 'syncing' : 'idle'}>
  <p class="lead">Jot a thought — it lands in <code>pages/</code> for the desktop to hatch.</p>

  {#if !session.state.dropboxConnected}
    <p class="hint">
      Connect Dropbox in <a use:link href={href('/settings')}>Settings</a> first — captures upload to
      your coop.
    </p>
  {/if}

  <textarea
    bind:value={text}
    rows="6"
    placeholder="What's on your mind?"
    onkeydown={(e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void save();
      }
    }}
  ></textarea>
  <button class="save" onclick={save} disabled={!text.trim()}>Save to coop</button>

  {#if justSaved}<p class="ok">Saved.</p>{/if}

  {#if capture.queue.length}
    <section class="queue">
      <h2>Pending</h2>
      <ul>
        {#each capture.queue as egg (egg.clientId)}
          <li class:err={egg.status === 'error'}>
            <span class="name">{humanize(egg.slug)}</span>
            <span class="status">
              {#if egg.status === 'uploading'}uploading…
              {:else if egg.status === 'queued'}waiting for a connection
              {:else}{egg.error ?? 'failed'}{/if}
            </span>
          </li>
        {/each}
      </ul>
      {#if capture.queue.some((e) => e.status !== 'uploading')}
        <button class="retry" onclick={() => capture.flush()}>Retry now</button>
      {/if}
    </section>
  {/if}
</Screen>

<style>
  .lead {
    margin-bottom: 14px;
    color: var(--ink-soft);
    font-size: 14px;
  }
  code {
    font-family: var(--mono);
    background: var(--paper);
    padding: 1px 5px;
    border-radius: 4px;
  }
  .hint {
    margin-bottom: 12px;
    color: var(--ink-soft);
    font-size: 13px;
  }
  .hint a {
    color: var(--rooster);
    text-decoration: underline;
  }
  textarea {
    width: 100%;
    resize: vertical;
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 12px;
    background: var(--cream-2);
    font-size: 16px;
  }
  .save {
    margin-top: 12px;
    border-radius: 12px;
    padding: 11px 20px;
    background: var(--rooster);
    color: var(--cream);
    font-family: var(--mono);
    font-weight: 700;
    font-size: 13px;
  }
  .save:disabled {
    opacity: 0.4;
  }
  .ok {
    margin-top: 10px;
    color: var(--leaf);
    font-family: var(--mono);
    font-size: 12px;
  }
  .queue {
    margin-top: 26px;
    border-top: 1px solid var(--line);
    padding-top: 16px;
  }
  .queue h2 {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-soft);
    margin-bottom: 10px;
  }
  .queue ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .queue li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
  }
  .queue li.err {
    color: var(--rooster-deep);
  }
  .name {
    font-family: var(--serif);
  }
  .status {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-faint);
  }
  .queue li.err .status {
    color: var(--rooster-deep);
  }
  .retry {
    margin-top: 12px;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 7px 14px;
    font-family: var(--mono);
    font-size: 12px;
  }
</style>
